import { Box, Button, Chip, IconButton, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CachedIcon from '@mui/icons-material/Cached';
import GroupsIcon from '@mui/icons-material/Groups';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';

const HEADER_BG = '#1e40af';
const HEADER_BORDER = '#60A5FA';

export default function FacHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);

  const isLive = location.pathname.startsWith('/app/live');
  const isSummary = location.pathname.startsWith('/app/summary');
  const isEPR = location.pathname.startsWith('/app/create/epr');
  const isScen = location.pathname.startsWith('/app/create/scenario');
  const isDash = location.pathname.startsWith('/');
  const isBuilder = isScen || isEPR;

  const getHeaderText = () => {
    if (location.pathname.startsWith('/app/create/epr')) return 'Set EPR Data';
    if (location.pathname.startsWith('/app/create/scenario')) return 'Set Scenario Data';
    if (location.pathname.startsWith('/app/live')) return 'Live Scenario';
    if (location.pathname.startsWith('/app/summary')) return 'Session Summary';
    if (location.pathname.startsWith('app/')) return 'Facilitator Dashboard';
    return 'Facilitator Dashboard';
  };

  const handleReturn = () => {
    navigate('/app');
  };

  const handleAccountClick = (event) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAccountAnchorEl(null);
  };

  const handleSettings = () => {
    handleAccountClose();
    navigate('/app/settings');
  };

  const handleLogout = async () => {
    handleAccountClose();
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleParticipant = () => {
    handleAccountClose();
    navigate('/app/participant');
  };

  const accountChip = (
    <Chip
      icon={<PersonOutlineIcon />}
      label={user?.email || '—'}
      onClick={handleAccountClick}
      clickable
      sx={{
        maxWidth: 320,
        color: 'white',
        bgcolor: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.22)',
        cursor: 'pointer',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiChip-icon': {
          color: 'white',
        },
        '&:hover': {
          bgcolor: 'rgba(255,255,255,0.16)',
        },
      }}
    />
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 72,
          px: 2,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          bgcolor: HEADER_BG,
          color: 'white',
          borderBottom: `1px solid ${HEADER_BORDER}`,
          zIndex: 1200,
        }}
      >
        {/* Left */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <img src="/ssiconfull.png" alt="logo" style={{ width:'160px'}} />
          {!isLive && !isDash && !isSummary && !isBuilder && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleReturn}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.35)',
                textTransform: 'none',
                bgcolor: 'rgba(255,255,255,0.06)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              Dashboard
            </Button>
          )}
        </Box>

        {/* Center */}
        <Box sx={{ textAlign: 'center', px: 2 }}>
          <Typography
            sx={{
              fontSize: '1.35rem',
              fontWeight: 700,
              letterSpacing: 0.2,
              whiteSpace: 'nowrap',
            }}
          >
            {getHeaderText()}
          </Typography>
        </Box>

        {/* Right */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          {isSummary || isBuilder ? 
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleReturn}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.35)',
                textTransform: 'none',
                bgcolor: 'rgba(255,255,255,0.06)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              Dashboard
            </Button> 
          : isLive ?
              <></>
          :
          (
            accountChip
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={accountAnchorEl}
        open={accountMenuOpen}
        onClose={handleAccountClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleParticipant}>
          <ListItemIcon>
            <CachedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Participant</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}